import React, { useCallback, useState } from 'react'
import { graphql } from 'gatsby'
import { Background, Container, Section } from '../../components/page'
import { InstructionsVideo } from '../../components/instructions-video'
import { Image } from '../../components/image'
import { LinkButton } from '../../components/button'
import { PlayIcon } from '../../components/icons'
import { useBrand } from '../../components/brand'
import { useSnippets } from '../../components/snippet'
import {
  HeroText,
  HeroTitle,
  HeroButtons,
  HeroStats,
  HeroCounter,
} from '../../components/hero'

import * as styles from './home-hero.module.scss'

export type HomeHeroProps = Queries.HomeHeroFragment

export const HomeHero = ({
  title,
  text,
  image,
  buttons,
  stats,
}: HomeHeroProps) => {
  const brand = useBrand()
  const s = useSnippets()

  return (
    <Section className={styles.section} variant="hero">
      <Container className={styles.container}>
        <div className={styles.content}>
          <div className={styles.info}>
            <HeroTitle color="light">{title}</HeroTitle>
            <HeroText color="light">{text?.text}</HeroText>
          </div>
          <HeroButtons>
            {buttons?.map((item, index) => (
              <LinkButton
                key={item?.id}
                link={item}
                color={index === 0 ? 'primary' : 'secondary'}
                variant={index === 0 ? 'fill' : null}
                size="lg"
              >
                {item?.title}
              </LinkButton>
            ))}
          </HeroButtons>
          <HeroStats>
            {stats?.map(
              (stat) =>
                stat && (
                  <HeroCounter
                    key={stat.id}
                    count={stat.count}
                    prefix={stat.prefix}
                    suffix={stat.suffix}
                    title={stat.title}
                    color="light"
                  />
                )
            )}
          </HeroStats>
        </div>
        <div className={styles.image}>
          <Image image={image} alt={image?.title!} loading="eager" />
          {brand?.instructionsVideo ? (
            <InstructionsVideo src={brand?.instructionsVideo}>
              <button className={styles.videoButton}>
                <PlayIcon />
                <span>{s.see_how_it_works}</span>
              </button>
            </InstructionsVideo>
          ) : null}
        </div>
      </Container>
      <Background variant="hero" />
    </Section>
  )
}

export const HomeHeroFragments = graphql`
  fragment HomeHeroImage on ContentfulAsset {
    __typename
    id
    title
    gatsbyImageData(width: 800)
  }
  fragment HomeHeroStats on ContentfulHomeHeroStats {
    __typename
    id
    title
    count
    prefix
    suffix
  }
  fragment HomeHero on ContentfulHomeHero {
    __typename
    id
    title
    text {
      text
    }
    image {
      ...HomeHeroImage
    }
    buttons {
      ...Link
    }
    stats {
      ...HomeHeroStats
    }
  }
`
